import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import { useGlobalSearchQuery } from "@graphql/global-search/globalSearch.g";
import { useDebounce } from "@uidotdev/usehooks";
import { ChangeEvent, KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Icon } from "@design-system/Icon/Icon";
import { Input } from "@design-system/Input/Input";
import { Resource } from "@design-system/Resource/Resource";
import { ScrollBox } from "@design-system/ScrollBox/ScrollBox";
import { Text } from "@design-system/Text/Text";
import { User } from "@design-system/User/User";

import { routes } from "@pages/routes";

import { GlobalSearchContext } from "./GlobalSearchContext";

import classes from "./GlobalSearch.module.css";

const SEARCH_DEBOUNCE_TIME = 300;

type SelectValue = string;

type Props = {
  onSelect?: (id: SelectValue) => void;
};

export const GlobalSearch = ({ onSelect }: Props) => {
  const navigate = useNavigate();
  const [reference, setReference] = useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);

  const globalSearchQuery = useGlobalSearchQuery(
    { search: debouncedSearch },
    { enabled: !!debouncedSearch },
  );

  const results = useMemo(() => {
    const clients = globalSearchQuery.data?.clients?.edges.map(
      (edge) => edge?.node,
    );
    const users = globalSearchQuery.data?.users?.edges.map(
      (edge) => edge?.node,
    );
    const employees = globalSearchQuery.data?.clientEmployees?.edges.map(
      (edge) => edge?.node,
    );
    const applications =
      globalSearchQuery.data?.applicationResources?.edges.map(
        (edge) => edge?.node,
      );

    return {
      clients,
      users,
      employees,
      applications,
    };
  }, [globalSearchQuery.data]);

  const isClientsFound = results.clients && results.clients.length > 0;
  const isUsersFound = results.users && results.users.length > 0;
  const isEmployeesFound = results.employees && results.employees.length > 0;
  const isApplicationsFound =
    results.applications && results.applications.length > 0;

  const isEmpty =
    !isClientsFound &&
    !isUsersFound &&
    !isEmployeesFound &&
    !isApplicationsFound;

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // setSearch("");
    }
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    elements: {
      reference,
    },
    middleware: [
      flip({
        padding: 12,
      }),
      offset({ mainAxis: 12 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover: false,
  });

  const focus = useFocus(context);
  const role = useRole(context, { role: "listbox" });
  const dismiss = useDismiss(context, {
    outsidePress: () => {
      setSearch("");
      return true;
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNavigation, focus, dismiss, role],
  );

  const handleSelect = useCallback(
    (id: string, index: number | null) => {
      onSelect?.(id);
      setSelectedIndex(index);
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Prevent space key from being captured by list navigation when typing
    if (event.key === " ") {
      event.stopPropagation();
    }
  }, []);

  const handleClientClick = (clientId?: string) => {
    if (!clientId) return;

    navigate(
      routes.portal.clients.client.record.$buildPath({ params: { clientId } }),
    );
    setIsOpen(false);
    setSearch("");
  };

  const handleUserClick = (userId?: string) => {
    if (!userId) return;
    navigate(routes.portal.users.user.$buildPath({ params: { userId } }));
    setIsOpen(false);
    setSearch("");
  };

  const handleEmployeeClick = (clientId: string, clientEmployeeId?: string) => {
    if (!clientEmployeeId) return;
    navigate(
      routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
        {
          params: { clientId, clientEmployeeId },
        },
      ),
    );
    setIsOpen(false);
    setSearch("");
  };

  const handleApplicationClick = (clientId: string, applicationId: string) => {
    navigate(
      routes.portal.clients.client.applications.application.dashboard.$buildPath(
        {
          params: {
            clientId,
            applicationId,
          },
        },
      ),
    );
    setIsOpen(false);
    setSearch("");
  };

  const dropdownContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, selectedIndex, getItemProps, handleSelect],
  );

  return (
    <>
      <div className={classes.search} ref={(el) => setReference(el)}>
        <Icon icon="faMagnifyingGlass" size="sm" />
        <Input
          {...getReferenceProps({
            type: "text",
            onChange: handleSearch,
            onKeyDown: handleKeyDown,
            value: search,
            placeholder: "Search...",
            "aria-autocomplete": "list",
            // This ensures that the input doesn't preserve min-width on
            // small screens and wraps correctly when there isn't enough space.
            size: 1,
          })}
          transparent
        />
      </div>
      <FloatingPortal>
        <GlobalSearchContext.Provider value={dropdownContext}>
          {isOpen && (
            <FloatingFocusManager
              context={context}
              modal={false}
              order={["reference", "floating"]}
              returnFocus={false}
            >
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <Card>
                  <ScrollBox stretch>
                    <div className={classes.list}>
                      <FloatingList
                        elementsRef={elementsRef}
                        labelsRef={labelsRef}
                      >
                        <Box direction="column" gap={16} padding="12px">
                          {isClientsFound && (
                            <Box direction="column" gap={12} stretch>
                              <Box gap={6}>
                                <Icon icon="faSuitcase" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Tenants
                                </Text>
                              </Box>
                              {results.clients?.map((client) => (
                                <Clickable
                                  onClick={() => handleClientClick(client?.id)}
                                  outline="xs"
                                  stretch
                                >
                                  <div className={classes.truncatedText}>
                                    <Resource
                                      key={client?.id}
                                      name={client?.name}
                                      skipLogo={true}
                                      domain={client?.domain}
                                    />
                                  </div>
                                </Clickable>
                              ))}
                            </Box>
                          )}

                          {isUsersFound && (
                            <Box direction="column" gap={12} stretch>
                              <Box gap={6}>
                                <Icon icon="faUser" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Staff members
                                </Text>
                              </Box>
                              {results.users?.map((user) => (
                                <Clickable
                                  onClick={() => handleUserClick(user?.id)}
                                  outline="xs"
                                  stretch
                                >
                                  <div className={classes.truncatedText}>
                                    <User
                                      key={user?.id}
                                      name={user?.name}
                                      email={user?.email}
                                    />
                                  </div>
                                </Clickable>
                              ))}
                            </Box>
                          )}

                          {isEmployeesFound && (
                            <Box direction="column" gap={12} stretch>
                              <Box gap={6}>
                                <Icon icon="faUserTie" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Users
                                </Text>
                              </Box>
                              {results.employees?.map((employee) => (
                                <Clickable
                                  onClick={() =>
                                    handleEmployeeClick(
                                      employee?.client?.id || "",
                                      employee?.id,
                                    )
                                  }
                                  outline="xs"
                                  stretch
                                >
                                  <Box direction="column" gap={4} stretch>
                                    <div className={classes.truncatedText}>
                                      <User
                                        key={employee?.id}
                                        name={employee?.name}
                                        email={employee?.email}
                                      />
                                    </div>
                                    <Box gap={6}>
                                      <Text color="textSecondary">
                                        <Icon icon="faSuitcase" size="sm" />
                                      </Text>
                                      <div className={classes.companyName}>
                                        <Text
                                          size={12}
                                          weight={600}
                                          color="textSecondary"
                                        >
                                          {employee?.client?.name}
                                        </Text>
                                      </div>
                                    </Box>
                                  </Box>
                                </Clickable>
                              ))}
                            </Box>
                          )}

                          {isApplicationsFound && (
                            <Box
                              direction="column"
                              gap={12}
                              stretch
                              fullStretch
                            >
                              <Box gap={6}>
                                <Icon icon="faShapes" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Applications
                                </Text>
                              </Box>
                              {results.applications?.map((application) => (
                                <Box direction="column" gap={12} fullStretch>
                                  <Resource
                                    key={application?.id}
                                    name={application?.productName || ""}
                                    subtext={application?.vendorName || ""}
                                    domain={
                                      application?.productDomainName || ""
                                    }
                                    skipLogo={
                                      application?.publicResource?.signedLogoUrl
                                        ? false
                                        : true
                                    }
                                    signedLogoUrl={
                                      application?.publicResource
                                        ?.signedLogoUrl || ""
                                    }
                                  />
                                  <Box direction="column" gap={8} fullStretch>
                                    {application?.applications?.edges.map(
                                      (clientApp) => (
                                        <Clickable
                                          onClick={() =>
                                            handleApplicationClick(
                                              clientApp?.node?.client?.id || "",
                                              clientApp?.node?.id || "",
                                            )
                                          }
                                          outline="sm"
                                          stretch
                                        >
                                          <Box gap={6}>
                                            <Text color="textSecondary">
                                              <Icon
                                                icon="faSuitcase"
                                                size="sm"
                                              />
                                            </Text>
                                            <Text
                                              size={12}
                                              weight={700}
                                              color="textSecondary"
                                              uppercase
                                            >
                                              {clientApp?.node?.client?.name ||
                                                ""}
                                            </Text>
                                          </Box>
                                        </Clickable>
                                      ),
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {isEmpty && !!debouncedSearch && (
                            <Box direction="column" gap={8}>
                              <Text
                                size={12}
                                weight={600}
                                color="textSecondary"
                              >
                                No results found
                              </Text>
                            </Box>
                          )}

                          {isEmpty && !debouncedSearch && (
                            <Box direction="column" gap={8}>
                              <Text
                                size={12}
                                weight={600}
                                color="textSecondary"
                              >
                                Search for anything...
                              </Text>
                            </Box>
                          )}
                        </Box>
                      </FloatingList>
                    </div>
                  </ScrollBox>
                </Card>
              </div>
            </FloatingFocusManager>
          )}
        </GlobalSearchContext.Provider>
      </FloatingPortal>
    </>
  );
};
